import {isLastDayOfMonth} from "date-fns";
import { parseDate } from "./dates";
import { INVOICE_ISSUE_METHOD } from "../constants/Invoice";
import { validateEditOrderForm, validateEditInvoiceForm } from "../lib/validation";
import { convertDollarToCent } from "./numbers";

export const formatEditInvoiceFormValues = (formValues, oldFormValues) => {
    const validationErr = validateEditInvoiceForm(formValues);
    if (validationErr) {
        return {
            formError: {message: 'The form contains error(s). Please correct them before saving.'},
            inlineErrors: validationErr
        };
    }
    const {
        has_milestones,
        milestones,
        tax_amount,
        discount_amount,
        shipping_amount,
        items,
        ...rest
    } = formValues;

    let billingMilestones;
    if (has_milestones) {
        billingMilestones = Object.values(milestones);
        // If issue_method is MANUAL or WITH_INVOICE, set issued_at to null
        // If fixed due date, set net_terms to 0
        // If net terms, set due_date to nil
        for (let i = 0; i < billingMilestones.length; i++) {
            const milestone = billingMilestones[i];
            const {
                issue_method,
                net_terms,
            } = milestone;
            if (issue_method !== INVOICE_ISSUE_METHOD.SPECIFIED) milestone['issued_at'] = null;
            if (net_terms === "fixed") {
                milestone['net_terms'] = 0;
            }
            else {
                milestone['due_date'] = null;
            }
        }
        // figure out which one is to be deleted
        if (oldFormValues) {
            const oldMilestones = oldFormValues?.invoice?.billing_milestones;
            if (oldMilestones && oldMilestones.length) {
                // looping through all the milestone this invoice had
                for (let i = 0; i < oldMilestones.length; i++) {
                    const thisId = oldMilestones[i].id;
                    // if we don't locate this id in the new set of mileston, pass destroy flag as true
                    if (!billingMilestones.find(milestone => milestone.id == thisId)) {
                        billingMilestones.push({...oldMilestones[i], _destroy: true});
                    }
                }
            }
        }
    }

    return {values: {
        ...formValues,
        items: items ?
            Object.values(items).map(({net_amount, description, variant_title, quantity, discount_bps, discount_percentage, unit}) => ({
                description: description,
                variant_title: variant_title,
                quantity: quantity,
                net_amount: convertDollarToCent(net_amount),
                discount_bps: discount_bps || 0,
                discount_percentage: discount_percentage || null,
                unit: unit || 'each'
            })) : [],
        discount_amount: convertDollarToCent(discount_amount),
        shipping_amount: convertDollarToCent(shipping_amount),
        tax_amount: convertDollarToCent(tax_amount),
        ...(has_milestones ? {billing_milestones_attributes: billingMilestones} : {}),
    }};
}

// deduce the period value based on start date and frequency
export const getDraftSeriesPeriod = ({
    period,
    start_date: startDate,
    frequency: frequency
}) => {
    // Skip if twice_every_month or yearly
    if (!["twice_every_month", "yearly"].includes(frequency)) {
        // start_date arrives as a string from the API but as a Date once it has been
        // through DateModal, and parseISO throws on anything but a string from date-fns 3.
        const parsedStartDate = parseDate(startDate);
        return parsedStartDate && isLastDayOfMonth(parsedStartDate) ? "last_day_of_month" : 'day_of';
    }
    if (frequency === "twice_every_month") {
        return (['first_and_fifteenth', 'fifteenth_and_last'].includes(period)) ? period : 'first_and_fifteenth';
    }
    return "day_of";
}

export const formatDraftInvoiceFormValues = (
    formValues,
    isGoodsCompany = false,
    orderId = null,
    oldFormValues = null
) => {
    const items = Object.assign({}, formValues?.items || {});
    (Object.keys(items)).forEach(lineId => {
        const item = items[lineId];
        // if line is empty, delete it
        if (!item?.description && !item?.variant_title && !item?.quantity && !item?.net_amount) {
            delete items[lineId];
        }
    });

    // validate form
    const validationErr = validateEditOrderForm({...formValues, items });
    if (validationErr) {
        return {
            formError: {message: 'The form contains error(s). Please correct them before saving.'},
            inlineErrors: validationErr
        };
    }

    // package up the data and send it to BE
    const {
        items: tempItems,
        discount_amount,
        shipping_amount,
        tax_amount,
        due_date,
        payment_terms,
        issued_at,
        use_order_address,
        has_milestones,
        milestones,
        tracking_infos,
        ...rest
    } = formValues;

    let dueDateObj;
    let due_date_format;
    if (payment_terms === "fixed") {
        due_date_format = "fixed";
        dueDateObj = { // Case "fixed" date, we pass back due_date, but set net terms as null
            using_net_terms: false,
            due_date,
            payment_terms: null,
            issued_at: (issued_at || null)
        };
    } else if (payment_terms === "default") {
        due_date_format = "default";
        dueDateObj = {
            using_net_terms: true,
            payment_terms: null,
            due_date: null,
            issued_at: (issued_at || null)
        };
    } else {
        due_date_format = "terms";
        let issue_date = (payment_terms === 0) ? null : issued_at;
        dueDateObj = {
            using_net_terms: true,
            payment_terms: payment_terms,
            due_date: null,
            issued_at: (issue_date || null)
        };
    }

    // Omit these fields and resulting object in payload
    const {
        billing_address_id,
        currency,
        description,
        discount_percentage,
        discount_type,
        identifier,
        recipient_id,
        shipping_address_id,
        draft_series,
        is_series,
        po_number,
    } = rest;

    let series;

    if (is_series) {
        const {period, start_date, has_end_date, end_date, frequency, ...rest} = draft_series;
        let newPeriod = getDraftSeriesPeriod(draft_series);
        let newStartDate = start_date;

        if (frequency === "twice_every_month") {
            newStartDate = new Date(start_date);
            newStartDate.setDate(period === "fifteenth_and_last" ? 15 : 1);
            newStartDate = newStartDate.toString();
        }
        series = {
            ...draft_series,
            end_date: has_end_date ? end_date : false,
            period: newPeriod,
            start_date: newStartDate ? newStartDate : start_date
        }
    }

    let isMilestoneDraftInvoice = has_milestones && !is_series;
    let billingMilestones;

    if (isMilestoneDraftInvoice) {
        billingMilestones = Object.values(milestones);
        // If issue_method is MANUAL or WITH_INVOICE, set issued_at to null
        // If fixed due date, set net_terms to 0
        // If net terms, set due_date to nil
        for (let i = 0; i < billingMilestones.length; i++) {
            const milestone = billingMilestones[i];
            const {
                issue_method,
                net_terms,
            } = milestone;
            if (issue_method !== INVOICE_ISSUE_METHOD.SPECIFIED) milestone['issued_at'] = null;
            if (net_terms === "fixed") {
                milestone['net_terms'] = 0;
            }
            else {
                milestone['due_date'] = null;
            }
        }
        // figure out which one is to be deleted
        if (oldFormValues) {
            const oldMilestones = oldFormValues?.billing_milestones;
            if (oldMilestones && oldMilestones.length) {
                // looping through all the milestone this invoice had
                for (let i = 0; i < oldMilestones.length; i++) {
                    const thisId = oldMilestones[i].id;
                    // if we don't locate this id in the new set of mileston, pass destroy flag as true
                    if (!billingMilestones.find(milestone => milestone.id == thisId)) {
                        billingMilestones.push({...oldMilestones[i], _destroy: true});
                    }
                }
            }
        }
    }

    const payload = {
        billing_address_id,
        currency,
        description,
        discount_percentage,
        discount_type,
        ...(orderId ? {id: orderId} : {}),
        identifier,
        recipient_id,
        po_number,
        ...(is_series ? { draft_series: series } : {}),
        ...(isGoodsCompany ? { shipping_address_id } : {})
    };
    const values = {
        ...payload,
        line_items: items ?
            Object.values(items).map(({net_amount, description, variant_title, quantity, discount_bps, discount_percentage, unit}) => ({
                description: description,
                variant_title: variant_title,
                quantity: quantity,
                net_amount: convertDollarToCent(net_amount),
                discount_bps: discount_bps || 0,
                discount_percentage: discount_percentage || null,
                unit: unit || 'each'
            })) : [],
        discount_amount: convertDollarToCent(discount_amount),
        shipping_amount: convertDollarToCent(shipping_amount),
        tax_amount: convertDollarToCent(tax_amount),
        ...dueDateObj,
        due_date_format: due_date_format,
        use_order_address: use_order_address,
        ...(isMilestoneDraftInvoice ? {billing_milestones_attributes: billingMilestones} : {}),
        ...(tracking_infos && tracking_infos.length > 0 ? {
            tracking_infos_attributes: tracking_infos
                .filter(info => info._destroy || info.id || info.number)
                .map(info => {
                    const { id, service_company, number, url, _destroy } = info;
                    return {
                        ...(id ? { id } : {}),
                        service_company,
                        number,
                        url,
                        ...(_destroy ? { _destroy: true } : {})
                    };
                })
        } : {})
    };

    return {values};
}
